// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// Animations
import { getTransitionPopoverProps } from "@/lib/animations"

// UI
import { cn } from "@/lib/utils"
import {
	Popover,
	PopoverGroup,
	PopoverButton,
	PopoverPanel,
	Transition,
} from "@/lib/ui"
import { DatePicker } from "./DatePicker"

// Icons
import { ChevronDownIcon } from "@/lib/icons"

export interface DateRangePickerProps {
	className?: string
	from?: string
	to?: string
	onSetFrom: Function
	onSetTo: Function
}

export function DateRangePicker({
	className,
	from,
	to,
	onSetFrom,
	onSetTo,
}: DateRangePickerProps) {
	return (
		<div className={cn("flex items-center gap-4", className)}>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromISO(String(from)).toFormat(
								dateFormat,
							)}
						</span>

						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-from"
							>
								<DatePicker
									selectedDate={DateTime.local()}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										onSetFrom(newDate.toJSDate())
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
			<span className="text-sm text-gray-500">to</span>
			<PopoverGroup className="flex items-center divide-x divide-gray-200">
				<Popover className="relative z-20 inline-block text-left">
					<PopoverButton
						className={cn(
							"group inline-flex items-center justify-center rounded-md",
							"border border-gray-200 bg-white px-4 py-2 text-sm text-gray-500",
							"focus:ring-primary-500 hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:outline-hidden",
						)}
					>
						<span className="mr-2">
							{DateTime.fromISO(String(to)).toFormat(dateFormat)}
						</span>
						<ChevronDownIcon
							className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
							aria-hidden="true"
						/>
					</PopoverButton>

					<Transition {...getTransitionPopoverProps()}>
						<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
							<div
								className="min-w-[300px]"
								data-testid="daterangepicker-to"
							>
								<DatePicker
									selectedDate={DateTime.local()}
									onSelectDate={(date) => {
										const newDate =
											DateTime.fromObject(date).endOf(
												"day",
											)
										onSetTo(newDate.toJSDate())
									}}
								/>
							</div>
						</PopoverPanel>
					</Transition>
				</Popover>
			</PopoverGroup>
		</div>
	)
}
